import {
  type FinishOption,
  type Price,
  type ProcessMaterialOption,
  certificatesAllowedFor,
  certificatesFor,
  finishPriceAdderFor,
  finishesFor,
  inspectionsFor,
  materialsFor,
  materialsGroupedByCategory,
} from '@misumi/shared'
import { type KeyboardEvent, useEffect, useId, useMemo, useRef, useState } from 'react'
import {
  BulbOutlined,
  CheckOutlined,
  DownOutlined,
  DownloadOutlined,
  EditOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  RightOutlined,
  SearchOutlined,
  ShareAltOutlined,
  UploadOutlined,
} from '../../../components/icons'
import { Button } from '../../../components/ui/Button'
import { Chip } from '../../../components/ui/Chip'
import { Modal } from '../../../components/ui/Modal'
import { Tabs } from '../../../components/ui/Tabs'
import type { TabBadge } from '../../../components/ui/Tabs'
import { PartAttachmentsSection } from '../attachments/PartAttachmentsSection'
import type { PartAttachmentsService } from '../attachments/usePartAttachments'
import {
  readBooleanAttr,
  readNumberAttr,
  readStringAttr,
  readStringListAttr,
} from '../config-attrs'
import { DEMO_SCENE_BY_PART_NAME } from '../demo-3d-scenes'
import { toDfmFindings } from '../dfm'
import type { PartProcess } from '../edit-part-config-service'
import { formatDate, formatPrice } from '../format'
import { PROCESS_GROUPS } from '../process-material-config'
import { normalizeProcess } from '../process-normalization'
import { DfmDiscrepancyList } from './DfmDiscrepancyList'
import { PartViewer3D } from './PartViewer3D'
import type { PartConfiguratorPricingFooter } from './part-configurator-pricing'
import type { PartConfiguratorPricingFooterProps } from './part-configurator-pricing'
import type { PartConfiguratorFormState } from './part-configurator-save'
import type { PartConfiguratorSaveSeam } from './part-configurator-save'
import type {
  PartConfiguratorCapabilities,
  PartConfiguratorSubject,
} from './part-configurator-subject'

export interface PartConfiguratorShellProps {
  readonly open: boolean
  readonly onClose: () => void
  /** UID-434/WS-A — the vertical-agnostic view model; see
   * `part-configurator-subject.ts` for the full rationale. Each entry point
   * (Part Library, RFQ single line, Cart line) supplies its own real
   * aggregate through its own adapter (`fromLibraryPartRow` is the one that
   * exists today). */
  readonly subject: PartConfiguratorSubject
  /** Which sections/controls this ENTRY POINT can ever show real data or
   * affordances for — see `PartConfiguratorCapabilities`'s own doc comment.
   * Distinct from the per-instance optional fields on `subject` itself. */
  readonly capabilities: PartConfiguratorCapabilities
  /** The injected save seam — Parts/Cart wrap the real save-then-reprice
   * `PATCH /parts/{partId}` (`EditPartConfigService`); RFQ wraps its own
   * live, non-mutating estimate + local draft-line write. Per ADR-0117 D9.1
   * and this ticket's human D4 ruling, the shell does NOT unify those two
   * seams — it only calls whichever one its caller supplied. */
  readonly saveSeam: PartConfiguratorSaveSeam
  /** UID-46 — "Apply to multiple parts" (dc.html:4948) applies THIS
   * shell's current, in-progress configuration (the same shape `handleSave`
   * saves — built by `buildFormState` below) to the caller's OWN active
   * multi-selection. Rendered ONLY when `capabilities.applyToMultiple` is
   * true (Part Library) — for every other entry point the control does not
   * render AT ALL, not even disabled (human ruling for this extraction).
   * When capability-enabled but this callback is absent, the button renders
   * disabled with a text explanation instead — never a silently-inert
   * enabled control (same optional-callback convention as
   * `onAddToCart`/`onSendToRfq` below). */
  readonly onApplyToMultiple?: (form: PartConfiguratorFormState) => void
  /** Shows the "Apply to multiple parts" button in its loading state while
   * the caller's own batch (triggered by `onApplyToMultiple` above) is
   * in-flight — independent of this shell's OWN `saving` (single-part save)
   * state. */
  readonly applyToMultipleBusy?: boolean
  /** UID-225 (`configView`'s "Add to cart · {price}" CTA) — same
   * optional-callback-disabled-with-title pattern as `onApplyToMultiple`;
   * absent, the button renders disabled with a text explanation rather than
   * silently inert or fabricating a cart mutation this shell doesn't own.
   * UID-506 — invoked with the FRESHLY SAVED subject: clicking the CTA
   * persists the current form via `saveSeam.save` first (identical
   * sequencing to `onSaveAndClose`), so whichever Finish/Material/etc. tile
   * is selected on screen — not just whatever was last explicitly saved —
   * is what actually reaches the cart. Never called if that save fails. */
  readonly onAddToCart?: (subject: PartConfiguratorSubject) => void
  /** UID-225 (`configView`'s "Send to RFQ" CTA) — same disabled-with-title
   * pattern as `onAddToCart` above. */
  readonly onSendToRfq?: (subject: PartConfiguratorSubject) => void
  /** Forwarded to the embedded `PartAttachmentsSection` (Drawing analysis
   * tab) — injectable for tests, same seam that component already exposes;
   * defaults to its own real BFF-backed service when omitted. Only
   * consulted when `capabilities.attachmentsCapable` and
   * `subject.attachmentsPartId` are both present. */
  readonly attachmentsService?: PartAttachmentsService
  /** UID-434/WS-D — the injected pricing/footer seam (see
   * `part-configurator-pricing.ts`). Absent (Part Library): the shell's own
   * built-in, `subject`-derived footer renders, unchanged. Present (RFQ):
   * that implementation renders instead, given the shell's live form state. */
  readonly pricingFooter?: PartConfiguratorPricingFooter
  /** UID-434/WS-D follow-up — "Configure next part" (dc.html:5700-5704/
   * 5718-5720). The caller's OWN "advance to the next line" signal:
   * `onAdvance` is invoked ONLY after this shell has persisted the current
   * subject's config via `saveSeam.save` (mirrors `onAddToCart`'s
   * save-then-act sequencing) — never called on a failed save. The shell
   * does not decide what "next" means; the caller (RFQ's `RfqReviewPage`)
   * re-targets by handing back a new `subject`/`line` for this SAME open
   * modal instance, which resets this shell's form fields via the existing
   * `[open, initial]` effect below — never a close+reopen. Absent (the
   * default, and every non-RFQ caller — Part Library/Cart) means there is
   * no next part at all: the built-in `ConfigPricingFooter` never renders
   * this CTA, and RFQ's own injected footer treats "not another line left
   * to Configure" identically to "not in an RFQ flow at all."
   *
   * Gate 5 Suggestion 10 — a single optional object, replacing the former
   * PAIRED `onNextPart`/`hasNextPart` booleans-and-callback. Those two could
   * independently take on combinations that meant nothing
   * (`{onNextPart: fn, hasNextPart: undefined}`, `{onNextPart: undefined,
   * hasNextPart: true}` both typechecked), and `RfqReviewPage` only ever
   * derived both from the ONE real fact — `nextLineId !== null` — anyway.
   * Forwarded to `pricingFooter` unchanged.
   *
   * Gate 5 C3 (structural fix) contract — `onAdvance` MUST result in a
   * changed `subject`/`initial` reference on this shell's next commit (in
   * practice: a changed `subject.id`). This shell holds `saving` true from
   * the moment `handleNextPart` calls `onAdvance` until the `[open,
   * initial]` reset effect below observes that new reference — that effect
   * is the ONLY recovery path; it does not depend on `nextPartCapable` or
   * on the focus-management effect further down. If `onAdvance` ever fails
   * to re-target — or closes the modal instead — the Save/Configure-next-part
   * actions stay disabled for the remainder of this open session. */
  readonly nextPart?: { readonly onAdvance: () => void } | undefined
  /**
   * UID-604 / ADR-0177 — forwarded VERBATIM to the injected `pricingFooter` as
   * {@link PartConfiguratorPricingFooterProps.partSeed}; see that field for the
   * full rationale (including why it is not a field on `subject`). The shell
   * itself never reads it — it is plumbing for a seam the shell only hosts, the
   * same posture it already takes toward `onSendToRfq`.
   *
   * Supplied only by RFQ's per-line entry point (`RfqLineConfiguratorModal`).
   * Absent everywhere else, which is correct: Parts'/Cart's built-in footer
   * prices off `subject.indicativePrice` and never calls the estimate endpoint,
   * so `EditConfigurationModal.tsx` stays byte-identical.
   */
  readonly partSeed?: string | undefined
}

type ConfigTabId = 'configuration' | 'feedback' | 'drawing'

/**
 * UID-93: real Fictiv process taxonomy (7 processes), flat but ordered per
 * `PROCESS_GROUPS` — instant/manual-quote processes first, then the
 * 48-hour-manual-quote group (`processGroupingUtils.ts`'s
 * INSTANT_MANUAL_PROCESSES_ORDER / MANUAL_QUOTE_PROCESSES_ORDER). The design
 * handoff's process select isn't visually grouped, so this stays a flat
 * `<select>` in that real order rather than adding `<optgroup>`s.
 */
const PROCESS_OPTIONS: ReadonlyArray<{ readonly id: PartProcess; readonly label: string }> = [
  ...PROCESS_GROUPS.instantManual,
  ...PROCESS_GROUPS.manualQuote,
]

const QUANTITY_MIN = 1
const QUANTITY_MAX = 1_000_000
const THREADED_HOLE_MIN = 0
const THREADED_HOLE_MAX = 1_000
const ADDITIONAL_REQUIREMENTS_MAX = 2000

/** The left column's 7 collapsible sections (dc.html §"body: two columns",
 * left). `technical` intentionally has no upload plumbing of its own — see
 * its render branch's doc comment for the reconciliation-with-"Drawing
 * analysis"-tab decision. */
type SectionId =
  | 'technical'
  | 'material'
  | 'finish'
  | 'threads'
  | 'inspections'
  | 'certificates'
  | 'additional'

/** Pure — module-scoped (not a closure) so `useEffect`'s dependency array
 * doesn't need to name it (biome's `useExhaustiveDependencies` correctly
 * flags a function redefined every render as a missing dep; a
 * module-scoped function has no such identity churn). A part that already
 * has a saved threaded-hole count auto-expands that section so the user sees
 * the current value rather than hiding it behind a collapsed row. */
function defaultOpenSections(threadedHoleCount: number | null): Record<SectionId, boolean> {
  return {
    technical: false,
    material: true,
    finish: true,
    threads: threadedHoleCount !== null,
    inspections: false,
    certificates: false,
    additional: false,
  }
}

/** UID-502 — the always-included baseline inspection (dc.html:5541's default
 * "Standard Inspection Report"). This is NOT a real catalog option:
 * `inspectionsFor` (packages/shared) never returns this string — the real,
 * ERP-derived per-material list only ever contains the two priced add-ons
 * below. Rendered as an informational, non-toggleable row (never part of
 * `inspections`/the saved request) since every quote gets it regardless of
 * material. */
const STANDARD_INSPECTION_NAME = 'Standard Inspection Report'
const STANDARD_INSPECTION_DESCRIPTION =
  'Parts are inspected for finish quality and dimensional accuracy using hand metrology.'

/** UID-502 — description text for the Inspections/Certificates option rows
 * (dc.html:5544/5571's `desc` line, omitted when these rows first shipped
 * because `inspectionsFor`/`certificatesFor` return bare `readonly string[]`
 * with no description field). Safe to hardcode, unlike a per-finish PRICE
 * (UID-503) — the real catalog (packages/shared/src/manufacturing-catalog.ts)
 * only ever emits exactly these 4 strings today (2 inspections, 2
 * certificates). Anything not in these maps (a future catalog addition)
 * falls back to a generic line rather than rendering blank — never a guess
 * at what a NEW real option actually does. */
const INSPECTION_DESCRIPTIONS: Readonly<Record<string, string>> = {
  'Advanced Inspection Report':
    'Parts are inspected using advanced technology, either CMM or laser scanner, to verify dimensional accuracy.',
}
const DEFAULT_INSPECTION_DESCRIPTION = 'Documentation provided with your parts.'

const CERTIFICATE_DESCRIPTIONS: Readonly<Record<string, string>> = {
  'Certificate of Conformity':
    'A certificate that verifies the part meets the requirements of your specifications (including finishing).',
  'Material Certification':
    'A material test report that provides traceability to the raw material used in your parts (including material composition).',
}
const DEFAULT_CERTIFICATE_DESCRIPTION = 'Documentation provided with your parts.'

/** UID-503 — the Finish tile's description line. Not per-finish: the design
 * prototype's own `finDescOf(f)` (dc.html:8266) only ever renders a per-finish
 * `f.desc` if one is set on the fixture, and NONE of the prototype's real
 * finishes carry one (`_matFinishes`, dc.html:8265) — every real finish falls
 * to this exact generic fallback line. Copied verbatim rather than a
 * per-finish description invented for the ~30 real ERP finish names, which
 * the design itself never authored either. */
const FINISH_TILE_DESCRIPTION = 'Secondary finishing operation, priced per part.'

/** UID-505 — the design's always-present, zero-cost "no secondary
 * operation" choice (dc.html:8265's `{id:'__std', name:'As-machined /
 * standard'}`), prepended to every material's tile grid regardless of which
 * real secondary finishes `finishesFor` returns. Used as BOTH the tile's
 * display name and its internal `finish` state value — distinct from the
 * empty string (which still means "no decision made yet"), so a deliberate
 * "no finish" choice is representable and shows as a selected tile. Mapped
 * back to the wire's `finish: null` in `buildFormState` (same sentinel
 * `RfqLineSpecification.finish`'s doc comment already documents `null` as:
 * "as machined / standard, no secondary operation" — a first-class answer,
 * not an unanswered question). */
const AS_MACHINED_FINISH = 'As-machined / standard'
const AS_MACHINED_DESCRIPTION = 'Best-in-class standard finish — no secondary operation.'

function descriptionFor(
  option: string,
  map: Readonly<Record<string, string>>,
  fallback: string,
): string {
  return map[option] ?? fallback
}

/**
 * UID-434/WS-A — the shared single-part configurator shell, extracted from
 * the pre-existing `EditConfigurationModal` (UID-163/C3, UID-225 fidelity
 * rebuild — `configView`, `Fictiv Platform.dc.html` ~4917-5225). Three tabs
 * (Configuration / Manufacturability feedback / Drawing analysis); calls the
 * injected `saveSeam` on save and reflects whatever it returns in-place
 * rather than closing immediately — the user reviews the outcome of their
 * own edit before dismissing.
 *
 * The Configuration tab is the design's two-column `configView` shell: a
 * scrollable left column of collapsible sections (each with a green-
 * check/gray-circle completion indicator) and a right column with a
 * viewer PLACEHOLDER + a contextual pricing footer that swaps between
 * `notReady`/`priced`/`rfq` off the subject's REAL `pricingProjection` (see
 * `resolvePricingFooterState`). The other two tabs keep their pre-existing
 * single-column layout and, per this extraction's human ruling (D3), stay
 * VISIBLE for every entry point — an entry point with no real DFM/
 * attachments data renders an honest "not available" empty state rather
 * than hiding the tab or fabricating a result.
 *
 * Uses the shared `Modal` shell (same choice as `PartPreviewDrawer`, B3/E1):
 * its own doc comment states the focus-trap/Escape/backdrop/return-focus
 * behavior comes for free from that shell, so a second, independent
 * `useFocusTrap` pass is NOT layered on top here.
 */
export function PartConfiguratorShell({
  open,
  onClose,
  subject,
  capabilities,
  saveSeam,
  onApplyToMultiple,
  applyToMultipleBusy = false,
  onAddToCart,
  onSendToRfq,
  attachmentsService,
  pricingFooter,
  nextPart,
  partSeed,
}: PartConfiguratorShellProps) {
  // Gate 5 Suggestion 10 — the collapsed `nextPart` prop, unpacked once so
  // the rest of this component's internals (largely unchanged) can keep
  // referring to a plain optional callback.
  const onNextPart = nextPart?.onAdvance
  const attrs = subject.configAttributes
  // `attrs` is derived from `subject` on every render, so depending on
  // `subject` alone (not `attrs` itself, which is a fresh object reference
  // each time) is the correct, non-thrashing dependency. Callers MUST pass
  // a stable `subject` reference across renders that haven't logically
  // changed it (e.g. memoize their adapter call) — see this file's sibling
  // `EditConfigurationModal.tsx` wrapper for the pattern.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  const initial = useMemo(
    () => ({
      process: normalizeProcess(readStringAttr(attrs, 'process') || undefined),
      quantity: readNumberAttr(attrs, 'quantity') ?? subject.quantity ?? 1,
      material: readStringAttr(attrs, 'material'),
      finish: readStringAttr(attrs, 'finish'),
      color: readStringAttr(attrs, 'color'),
      threadedHoleCount: readNumberAttr(attrs, 'threadedHoleCount'),
      inspections: readStringListAttr(attrs, 'inspections'),
      certificates: readStringListAttr(attrs, 'certificates'),
      additionalRequirements: readStringAttr(attrs, 'additionalRequirements'),
      customInspection: readBooleanAttr(attrs, 'customInspection'),
    }),
    [subject],
  )

  const [activeTab, setActiveTab] = useState<ConfigTabId>('configuration')
  const [process, setProcess] = useState<PartProcess>(initial.process)
  const [quantity, setQuantity] = useState(initial.quantity)
  const [material, setMaterial] = useState(initial.material)
  const [materialQuery, setMaterialQuery] = useState(initial.material)
  const [materialOpen, setMaterialOpen] = useState(false)
  const [materialHighlight, setMaterialHighlight] = useState(0)
  const [finish, setFinish] = useState(initial.finish)
  const [color, setColor] = useState(initial.color)
  const [threadedHoleCount, setThreadedHoleCount] = useState(initial.threadedHoleCount)
  // Distinct from `openSections.threads` (the ACCORDION disclosure) — this
  // is the pre-rebuild "Configure threads" secondary reveal (dc.html:159)
  // nested one level deeper inside that accordion's panel, so a part with a
  // saved count auto-opens the stepper rather than requiring 2 clicks to
  // see it.
  const [threadsFieldsOpen, setThreadsFieldsOpen] = useState(initial.threadedHoleCount !== null)
  const [inspections, setInspections] = useState<readonly string[]>(initial.inspections)
  const [certificates, setCertificates] = useState<readonly string[]>(initial.certificates)
  const [additionalRequirements, setAdditionalRequirements] = useState(
    initial.additionalRequirements,
  )
  // UID-434/WS-D — RFQ-only routing state; see `PartConfiguratorFormState.
  // customInspection`'s doc comment. Rendered only when
  // `capabilities.customInspectionCapable` is true, but tracked
  // unconditionally here (like every other field) so a subject that already
  // carries it (e.g. re-opening an already-configured RFQ line) seeds
  // correctly regardless.
  const [customInspection, setCustomInspection] = useState(initial.customInspection)
  const [openSections, setOpenSections] = useState<Record<SectionId, boolean>>(() =>
    defaultOpenSections(initial.threadedHoleCount),
  )
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({})
  const [saving, setSaving] = useState(false)
  const [saveBanner, setSaveBanner] = useState<{
    readonly kind: 'success' | 'error'
    readonly message: string
  } | null>(null)
  const [refreshedSubject, setRefreshedSubject] = useState<PartConfiguratorSubject | null>(null)

  // Reset every field to the subject's current stored config whenever the
  // modal opens (or a different subject is passed in while open) — never
  // carries stale edits from a previous subject/open into a new session.
  useEffect(() => {
    if (!open) return
    setActiveTab('configuration')
    setProcess(initial.process)
    setQuantity(initial.quantity)
    setMaterial(initial.material)
    setMaterialQuery(initial.material)
    setMaterialOpen(false)
    setFinish(initial.finish)
    setColor(initial.color)
    setThreadedHoleCount(initial.threadedHoleCount)
    setThreadsFieldsOpen(initial.threadedHoleCount !== null)
    setInspections(initial.inspections)
    setCertificates(initial.certificates)
    setAdditionalRequirements(initial.additionalRequirements)
    setCustomInspection(initial.customInspection)
    setOpenSections(defaultOpenSections(initial.threadedHoleCount))
    setFieldErrors({})
    setSaveBanner(null)
    setRefreshedSubject(null)
    // Gate 5 C3 (structural fix) — this effect fires on exactly the
    // re-target commit (`open`/`initial` change together whenever the
    // subject being configured changes, including the RFQ "Configure next
    // part" re-target), is capability-agnostic (fires for Cart/Part Library
    // callers too, where it's a harmless no-op since `saving` is already
    // false), and doesn't care *why* the subject changed. Recovery from the
    // `advancing`/`saving` state therefore no longer depends on the
    // RFQ-specific focus effect below ever running — see that effect's own
    // comment and the `onAdvance`/`onNextPart` contract note on this
    // component's props for the invariant this closes.
    setSaving(false)
  }, [open, initial])

  function toggleSection(id: SectionId) {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  // Roving-tabindex focus targets for the Finish tile grid's `role="radio"`
  // divs (dc.html:5483-5509) — arrow-key navigation moves focus AND
  // selection together, same as a native radiogroup.
  const finishTileRefs = useRef<Array<HTMLDivElement | null>>([])

  // UID-434/WS-D follow-up — "Configure next part" focus management
  // (accessibility-wcag skill: focus must never fall through to
  // `document.body` on dynamic content changes). The dialog itself stays
  // mounted across a re-target (never a close+reopen — see `onNextPart`'s
  // own doc comment), so `Modal`'s OWN focus-on-open effect (keyed only on
  // `[open, initialFocusRef]`) never re-fires for this transition. This
  // effect is the one that does: whenever `subject.id` changes WHILE the
  // dialog is already open, the just-clicked "Configure next part" button
  // is about to unmount (the footer's `ready` gate flips false again for
  // the new, unconfigured line) — move focus to the DIALOG CONTAINER
  // (looked up via `.closest('[role="dialog"]')` off the always-mounted
  // announcement node below), the exact same target `Modal.tsx`'s own
  // focus-on-open effect uses, instead of letting the browser strand it on
  // `<body>`.
  //
  // Gate 5 W5 — this used to focus the `sr-only` announcement `<output>`
  // itself. That passed "never strands on body" but broke WCAG 2.2 AA 2.4.7
  // (Focus Visible): a sighted keyboard user got no visible focus indicator
  // at all, since the focused node is invisible by design. Focusing the
  // dialog container instead gives keyboard users the same visible focus
  // treatment `Modal` already applies on open, and sidesteps a screen
  // reader potentially double-announcing a live region that is ALSO the
  // focused node while its text is changing.
  //
  // Gate 5 W6 — both this effect's work and the announcement node itself
  // are gated on a "is this modal instance even shaped for a next-part flow
  // at all" signal. Cart (`CartLineRow.tsx`) and Part Library
  // (`PartDetailPage.tsx`) never pass `onNextPart` — for them this is
  // dormant today only because `subject.id` never changes while `open`
  // stays true, a coincidence, not a guarantee. Gating on the capability
  // signal itself (the same optional-callback convention every other
  // caller-supplied seam on this shell already uses) means a future
  // one-render `open` false→true + `subject.id` change for a NON-RFQ caller
  // can never fire this or override `Modal`'s own focus-on-open.
  //
  // Deliberately a STICKY ref, not a plain `Boolean(onNextPart)` read fresh
  // every render: unlike `onAddToCart`/`onSendToRfq` (fixed per entry
  // point), `onNextPart` is itself per-render OPTIONAL even within a single
  // RFQ session — `RfqReviewPage` hands back `undefined` for it the moment
  // there is no OTHER line left needing Configure (`nextLineId !== null ?
  // (...) : undefined`), which is exactly the render where the buyer has
  // just advanced onto that last remaining line and most needs the
  // announcement/focus-management this gate protects. Latching `true` the
  // first time `onNextPart` is ever defined for this mounted instance keeps
  // the RFQ modal "capable" for the rest of its lifetime while Cart/Part
  // Library — which never pass it at all — stay gated off permanently.
  const nextPartCapableRef = useRef(Boolean(onNextPart))
  // Render-phase ref write, deliberately — safe because it's monotonic
  // (false -> true, never back) and idempotent (re-running this exact write
  // under StrictMode's double-render, or under concurrent-rendering
  // discarding a render, changes nothing observable). Not modeled as
  // `useState`+`useEffect` because that would delay "capable" by one extra
  // commit relative to the render that first receives `onNextPart` — this
  // ref needs to be readable in the SAME render, before the effects below
  // that depend on `nextPartCapable` run.
  if (onNextPart) nextPartCapableRef.current = true
  const nextPartCapable = nextPartCapableRef.current
  const previousSubjectIdRef = useRef(subject.id)
  const nextPartAnnouncementRef = useRef<HTMLOutputElement | null>(null)
  useEffect(() => {
    if (nextPartCapable && open && previousSubjectIdRef.current !== subject.id) {
      const dialog = nextPartAnnouncementRef.current?.closest<HTMLElement>('[role="dialog"]')
      dialog?.focus()
      // Gate 5 C3 (structural fix) — `saving` recovery no longer lives here.
      // This effect is now purely about focus management on re-target; the
      // `[open, initial]` reset effect above is the single point of
      // recovery for `saving`, so it can never depend on whether a
      // capability-gated, RFQ-specific effect happens to run.
    }
    previousSubjectIdRef.current = subject.id
  }, [open, subject.id, nextPartCapable])

  const processId = useId()
  const quantityId = useId()
  const materialInputId = useId()
  const materialListId = useId()
  const materialHeadingId = useId()
  const materialPanelId = useId()
  const finishId = useId()
  const finishHeadingId = useId()
  const finishPanelId = useId()
  const threadsHeadingId = useId()
  const threadsPanelId = useId()
  const threadsInputId = useId()
  const inspectionsHeadingId = useId()
  const inspectionsPanelId = useId()
  const certificatesHeadingId = useId()
  const certificatesPanelId = useId()
  const additionalHeadingId = useId()
  const additionalPanelId = useId()
  const additionalReqId = useId()
  const technicalHeadingId = useId()
  const technicalPanelId = useId()

  const materialChosen = material.trim().length > 0

  // UID-93: the real, process-scoped material list (customer-visible only —
  // see process-material-config.ts's header for the replicated ERP rules).
  const materialsForProcess = materialsFor(process)
  const selectedMaterialOption = materialsForProcess.find((m) => m.name === material)
  const availableFinishes = finishesFor(process, material)
  // UID-505 — the design's "As-machined / standard" tile (see the constant's
  // doc comment) is always offered first, regardless of which real
  // secondary finishes this material has — it is not part of the ERP-
  // derived catalog `finishesFor` returns, so it is prepended here rather
  // than sourced from it.
  const finishTileOptions: readonly FinishOption[] = [
    { id: '__as_machined', name: AS_MACHINED_FINISH },
    ...availableFinishes,
  ]
  // Certificates are never offered for urethane_casting — a real
  // compatibility rule (CERTIFICATION_ALLOWED_PROCESS_TYPES), not a
  // per-material quirk, so it's keyed off the PROCESS, not the material.
  const certificatesAllowed = certificatesAllowedFor(process)
  const availableCertificates = certificatesFor(process, material)
  const availableInspections = inspectionsFor(process, material)

  // The material dropdown is grouped by family/category (dc.html:5471). The
  // grouping RULE lives in `@misumi/shared` (`materialsGroupedByCategory`) so
  // this shell and any other vertical's material combobox share one owner.
  const materialGroups = useMemo(() => materialsGroupedByCategory(process), [process])

  // The same groups with each group's options narrowed to the search query;
  // groups with no remaining match drop out entirely (the design's empty state
  // is a single flat "No materials match", not a list of empty headers).
  const filteredMaterialGroups = useMemo(() => {
    const q = materialQuery.trim().toLowerCase()
    if (!q) return materialGroups
    return materialGroups
      .map((group) => ({
        category: group.category,
        materials: group.materials.filter((m) => m.name.toLowerCase().includes(q)),
      }))
      .filter((group) => group.materials.length > 0)
  }, [materialGroups, materialQuery])

  // Flat list backing keyboard navigation + `aria-activedescendant`. Its order
  // is the grouped VISUAL order (headers aside), so ArrowUp/Down move through
  // options exactly as they read on screen, crossing group boundaries.
  const filteredMaterials = useMemo(
    () => filteredMaterialGroups.flatMap((group) => group.materials),
    [filteredMaterialGroups],
  )

  // Pre-flattened render rows: a header row per group, then its option rows,
  // each option carrying its index into `filteredMaterials` so the highlight
  // and `aria-activedescendant` wiring stays a plain index lookup.
  const materialRows = useMemo(() => {
    const rows: Array<
      | { readonly kind: 'header'; readonly category: string }
      | { readonly kind: 'option'; readonly option: ProcessMaterialOption; readonly index: number }
    > = []
    let index = 0
    for (const group of filteredMaterialGroups) {
      rows.push({ kind: 'header', category: group.category })
      for (const option of group.materials) {
        rows.push({ kind: 'option', option, index })
        index += 1
      }
    }
    return rows
  }, [filteredMaterialGroups])

  /** Clearing/changing material invalidates any prior finish (and its
   * color) choice — they're this material's own options, not global. */
  function selectMaterial(option: { readonly name: string }) {
    setMaterial(option.name)
    setMaterialQuery(option.name)
    setMaterialOpen(false)
    setFinish('')
    setColor('')
    setCertificates([])
    setInspections([])
  }

  /** Changing process invalidates the material (and everything downstream
   * of it) — a material from the old process's list may not exist, or mean
   * something different, under the new one. */
  function handleProcessChange(next: PartProcess) {
    setProcess(next)
    setMaterial('')
    setMaterialQuery('')
    setFinish('')
    setColor('')
    setCertificates([])
    setInspections([])
  }

  function handleMaterialKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setMaterialOpen(true)
      setMaterialHighlight((i) => Math.min(i + 1, Math.max(filteredMaterials.length - 1, 0)))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setMaterialHighlight((i) => Math.max(i - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const chosen = filteredMaterials[materialHighlight]
      if (chosen) selectMaterial(chosen)
    } else if (event.key === 'Escape' && materialOpen) {
      // Close only the listbox, not the whole dialog (ARIA combobox
      // pattern) — the dialog's own Escape handler (Modal.tsx) is a
      // document-level listener, so this must not let the event bubble.
      event.stopPropagation()
      setMaterialOpen(false)
    }
  }

  /** A finish's own colors are its options, not global — selecting a new
   * finish tile (click, or via keyboard below) always invalidates any prior
   * color choice, same rule the old `<select>` enforced. */
  function selectFinish(option: FinishOption) {
    setFinish(option.name)
    setColor('')
  }

  /** Custom `role="radio"` keyboard handling for the Finish tile grid
   * (dc.html:5483-5509) — arrow keys move focus AND selection together,
   * matching native `<input type="radio">` roving-tabindex behavior, since
   * the tiles can't be real radio inputs (each selected tile nests its own
   * `role="radio"` color chips, and interactive controls can't nest inside
   * a `<label>`/native radio the way this design's markup requires). */
  function handleFinishTileKeyDown(event: KeyboardEvent<HTMLDivElement>, index: number) {
    const count = finishTileOptions.length
    if (count === 0) return
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      const option = finishTileOptions[index]
      if (option) selectFinish(option)
      return
    }
    let nextIndex: number | null = null
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (index + 1) % count
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (index - 1 + count) % count
    } else if (event.key === 'Home') {
      nextIndex = 0
    } else if (event.key === 'End') {
      nextIndex = count - 1
    }
    if (nextIndex === null) return
    event.preventDefault()
    const next = finishTileOptions[nextIndex]
    if (next) {
      selectFinish(next)
      finishTileRefs.current[nextIndex]?.focus()
    }
  }

  function toggleListItem(list: readonly string[], item: string): readonly string[] {
    return list.includes(item) ? list.filter((i) => i !== item) : [...list, item]
  }

  /** The single-part form state this shell's current UI maps to — shared by
   * `handleSave` (below) and `onApplyToMultiple` (the "Apply to multiple
   * parts" button), so the multi-part caller applies EXACTLY the same form
   * state this shell would itself save, not a second, drifting projection
   * of the same UI state. */
  function buildFormState(): PartConfiguratorFormState {
    return {
      process,
      quantity,
      material: materialChosen ? material : null,
      finish: materialChosen && finish && finish !== AS_MACHINED_FINISH ? finish : null,
      color: materialChosen && finish && color ? color : null,
      threadedHoleCount: threadedHoleCount === null ? null : threadedHoleCount,
      inspections: inspections.length > 0 ? [...inspections] : null,
      certificates: materialChosen && certificates.length > 0 ? [...certificates] : null,
      additionalRequirements: additionalRequirements.trim() ? additionalRequirements : null,
      customInspection,
    }
  }

  // TODO(runSave-extraction): extract shared save-then-act body; see Gate 5
  // review S12. This function, `handleAddToCart`, and `handleNextPart` below
  // share a near-identical save-then-act body (setSaving/setSaveBanner/
  // setFieldErrors, call `saveSeam.save`, branch on ok/field_errors/error).
  // A shared `runSave` extraction is real and would very likely have
  // prevented Gate 5 C3 (the `handleNextPart`-only staleness bug) — but
  // extracting it now, in the same diff as that fix, would touch all three
  // call sites of this already-2000-line component and make this review
  // unreviewable. Left as three near-duplicates deliberately.
  async function handleSave(andClose: boolean) {
    setSaving(true)
    setSaveBanner(null)
    setFieldErrors({})
    try {
      const form = buildFormState()
      const outcome = await saveSeam.save(form, andClose)

      if (outcome.ok) {
        setRefreshedSubject(outcome.subject)
        if (andClose) {
          onClose()
          return
        }
        setSaveBanner({ kind: 'success', message: 'Configuration saved.' })
        // Gate 5 C1 — only auto-switch to the Manufacturability feedback tab
        // for an entry point that can actually show something there
        // (`capabilities.dfmCapable`). RFQ (`dfmCapable: false`, no
        // `dfmSummary` ever) rendered ONLY the "isn't available at this
        // stage" empty state on this tab — before this ticket that was
        // unreachable because a save always closed the modal; leaving the
        // modal open on "Save this configuration" exposed it as a real
        // stranding defect. The success banner above is unconditional and
        // stays visible regardless of which tab is active, so a non-DFM
        // caller still gets an in-modal confirmation without being moved
        // off the form they're still working in.
        if (capabilities.dfmCapable) {
          setActiveTab('feedback')
        }
        return
      }

      if (outcome.kind === 'field_errors') {
        const byField: Record<string, string> = {}
        for (const err of outcome.errors) {
          byField[err.field] = err.detail
        }
        setFieldErrors(byField)
        setSaveBanner({
          kind: 'error',
          message: 'One or more configuration fields are invalid. See the details below.',
        })
        return
      }

      setSaveBanner({ kind: 'error', message: outcome.message })
    } finally {
      setSaving(false)
    }
  }

  // UID-506 — "Add to cart" used to call `onAddToCart` directly against the
  // modal-open snapshot, so a Finish/Material tile picked but never
  // explicitly "Save"d never reached the cart at all — the button LOOKED
  // wired (once `onAddToCart` is actually supplied by the caller) but added
  // whatever configuration was last persisted, not whatever is currently
  // selected on screen. Mirrors `onSaveAndClose`'s own save-then-act
  // sequencing: persist the CURRENT form first (same `saveSeam.save` call,
  // same field-error/banner handling as a normal save), and only call
  // `onAddToCart` — then close — once that save actually lands.
  // TODO(runSave-extraction): another near-identical save-then-act body;
  // see Gate 5 review S12 (same extraction as `handleSave`'s TODO above).
  async function handleAddToCart() {
    if (!onAddToCart) return
    setSaving(true)
    setSaveBanner(null)
    setFieldErrors({})
    try {
      const form = buildFormState()
      const outcome = await saveSeam.save(form, true)

      if (outcome.ok) {
        setRefreshedSubject(outcome.subject)
        onAddToCart(outcome.subject)
        onClose()
        return
      }

      if (outcome.kind === 'field_errors') {
        const byField: Record<string, string> = {}
        for (const err of outcome.errors) {
          byField[err.field] = err.detail
        }
        setFieldErrors(byField)
        setSaveBanner({
          kind: 'error',
          message: 'One or more configuration fields are invalid. See the details below.',
        })
        return
      }

      setSaveBanner({ kind: 'error', message: outcome.message })
    } finally {
      setSaving(false)
    }
  }

  // UID-434/WS-D follow-up — "Configure next part". Same save-then-act
  // sequencing as `handleAddToCart`/`handleSave(true)`: persist whatever is
  // CURRENTLY selected on screen first (never the last explicitly-saved
  // state), and only advance — via the caller's `onNextPart` — once that
  // save actually lands. Never closes the dialog itself (unlike
  // `handleSave(true)`): the caller advances by hanging a NEW `subject` off
  // this same open modal, which the `[open, initial]` effect above resets
  // this shell's fields against.
  //
  // Gate 5 C3 — TWO deliberate departures from `handleSave`/`handleAddToCart`'s
  // otherwise-identical shape, both load-bearing:
  //
  //  1. Does NOT call `setRefreshedSubject(outcome.subject)`. `onNextPart()`
  //     immediately below hands control back to the caller (`RfqReviewPage`),
  //     which re-targets this same open modal at the NEXT line by passing a
  //     new `subject` prop — and React 18 batches that state update together
  //     with whatever this function still does in this same tick. Setting
  //     `refreshedSubject` here would have it hold the JUST-SAVED (now
  //     PREVIOUS) line's subject going into that very commit, so
  //     `shownSubject` (`refreshedSubject ?? subject`) would resolve to the
  //     OLD line for one full render while `subject` itself (and everything
  //     keyed off it directly — the header, the focus-announcement text) had
  //     already moved to the NEW line. `refreshedSubject` is a display-only
  //     "what did the save seam just hand back" cache with no purpose once
  //     the shell is about to show a DIFFERENT line entirely — it is not
  //     read again once `onNextPart` fires, since the caller's next render
  //     replaces `subject` outright and the `[open, initial]` reset effect
  //     clears `refreshedSubject` back to `null` anyway.
  //  2. Does NOT reset `saving` back to `false` in a `finally` on this branch
  //     — it is left `true` deliberately. The commit that lands `onNextPart`'s
  //     new `subject`/`initial` is the SAME commit this function's remaining
  //     state updates land in; recovery now happens in the `[open, initial]`
  //     reset effect above the very next time it re-runs for this re-target,
  //     NOT in the RFQ-specific focus effect. This closes the window
  //     security flagged where the Save/Configure-next-part buttons would
  //     otherwise re-enable in the same frame the un-reset (about to be
  //     overwritten) form fields are still showing, AND — per Gate 5 C3
  //     (structural fix) — makes that recovery independent of whether
  //     `onAdvance` changes `subject.id`, whether it's `nextPartCapable`, or
  //     whether the focus effect runs at all. `onAdvance` MUST still result
  //     in a changed `subject`/`initial` reference on the next commit (see
  //     the `nextPart` prop doc on this component and the type declarations
  //     in `part-configurator-pricing.ts`, `RfqLineConfiguratorModal.tsx`,
  //     and the `RfqReviewPage` call site) — but a failure to do so now
  //     leaves the shell stuck showing the previous subject's stale fields
  //     with `saving` still `true`, rather than silently re-enabling actions
  //     on a form no reset ever ran against.
  //
  // A `key={subject.id}` on the shell (forcing a full remount instead of the
  // in-place `[open, initial]` reset) was considered as an alternative that
  // would make this whole class of stale-frame bug structurally impossible —
  // but it would also unmount/remount `Modal`, tearing down and rebuilding
  // its focus trap on every "Configure next part" click and firing `Modal`'s
  // own focus-on-open effect again (fighting the bespoke announcement/focus
  // effect this ticket already needs for W5/W6). Keeping one mounted
  // instance and fixing the two specific staleness sources above is the
  // smaller, more targeted change; revisit the `key`-remount approach only if
  // a future defect shows this pair of fixes isn't sufficient.
  //
  // TODO(runSave-extraction): a third near-identical save-then-act body;
  // see Gate 5 review S12 (same extraction as `handleSave`'s TODO above).
  // Arguably the strongest evidence FOR that extraction — this function's
  // own C3 bug was exactly the kind of per-copy drift a shared `runSave`
  // would close.
  async function handleNextPart() {
    if (!onNextPart) return
    setSaving(true)
    setSaveBanner(null)
    setFieldErrors({})
    let advancing = false
    try {
      const form = buildFormState()
      const outcome = await saveSeam.save(form, false)

      if (outcome.ok) {
        advancing = true
        onNextPart()
        return
      }

      if (outcome.kind === 'field_errors') {
        const byField: Record<string, string> = {}
        for (const err of outcome.errors) {
          byField[err.field] = err.detail
        }
        setFieldErrors(byField)
        setSaveBanner({
          kind: 'error',
          message: 'One or more configuration fields are invalid. See the details below.',
        })
        return
      }

      setSaveBanner({ kind: 'error', message: outcome.message })
    } finally {
      if (!advancing) setSaving(false)
    }
  }

  const shownSubject = refreshedSubject ?? subject
  // UID-434/WS-D — the injected pricing/footer seam; absent (Part Library),
  // this falls back to the shell's own built-in, `subject`-derived footer,
  // unchanged.
  const PricingFooter = pricingFooter ?? ConfigPricingFooter
  const tabItems: ReadonlyArray<{
    readonly id: ConfigTabId
    readonly label: string
    readonly badge?: TabBadge
  }> = [
    { id: 'configuration', label: 'Configuration' },
    {
      id: 'feedback',
      label: 'Manufacturability feedback',
      ...(shownSubject.dfmSummary?.findings?.length
        ? {
            badge: {
              kind: 'count',
              count: shownSubject.dfmSummary.findings.length,
            } as TabBadge,
          }
        : {}),
    },
    { id: 'drawing', label: 'Drawing analysis' },
  ]

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit configuration — ${subject.displayName}`}
      maxWidth={1600}
      className="modal-config"
      headerContent={
        <div className="part-config-file-header">
          <span className="part-config-badge-version">V1</span>
          <span className="part-config-filename">{subject.displayName}</span>
          <button
            type="button"
            className="part-config-header-link-btn"
            disabled
            title="Version history isn't available for this part yet"
          >
            View version history
          </button>
          <span className="part-config-header-spacer" />
          <button
            type="button"
            className="part-config-header-icon-btn"
            disabled
            aria-label="Share"
            title="Sharing isn't available for this part yet"
          >
            <ShareAltOutlined aria-hidden="true" />
          </button>
          <button
            type="button"
            className="part-config-header-icon-btn"
            disabled
            aria-label="Download"
            title="Download isn't available for this part yet"
          >
            <DownloadOutlined aria-hidden="true" />
          </button>
          <span className="part-config-header-divider" aria-hidden="true" />
        </div>
      }
    >
      {/* UID-434/WS-D follow-up — "Configure next part" announcement.
          Mounted only when `nextPartCapable` (Gate 5 W6 — see the focus
          effect above) so Cart/Part Library, which have no "next part"
          concept, never get this live region at all. Visually hidden
          (`sr-only`) since the design has no visible "now configuring X"
          treatment, but announced for screen-reader users via `<output>`'s
          OWN IMPLICIT ARIA mapping to `role="status"` — a LIVE REGION ONLY
          now (Gate 5 W5): focus itself moves to the dialog container (the
          effect above), never to this node, so a sighted keyboard user
          keeps a visible focus indicator and a screen reader isn't asked to
          announce a live-region update on the exact node that also just
          received focus.
          Gate 5 W5 — a previous version of this comment claimed
          `role="status"` was set EXPLICITLY here when it was not; stated as
          IMPLICIT now instead (an explicit `role="status"` on `<output>` is
          redundant per WAI-ARIA — `lint/a11y/noRedundantRoles` — since the
          element's own default role already computes to exactly that), so
          the two can't drift again in either direction. */}
      {nextPartCapable && (
        <output ref={nextPartAnnouncementRef} className="sr-only">
          {`Now configuring ${subject.displayName}.`}
        </output>
      )}
      {/* Rendered OUTSIDE the tab panels (not scoped to the Configuration
          tab's own body) — "Save this configuration" auto-switches to the
          Manufacturability feedback tab on success, and the banner needs
          to still be visible after that switch, not vanish with the
          now-hidden Configuration tabpanel. */}
      {saveBanner && (
        <p
          role={saveBanner.kind === 'error' ? 'alert' : 'status'}
          className={
            saveBanner.kind === 'error'
              ? 'part-config-banner part-config-banner-error'
              : 'part-config-banner part-config-banner-success'
          }
        >
          {saveBanner.message}
        </p>
      )}
      <Tabs
        items={tabItems}
        activeId={activeTab}
        onChange={(id) => setActiveTab(id as ConfigTabId)}
        aria-label="Part configuration sections"
      >
        {(activeId) => {
          if (activeId === 'feedback') {
            const dfm = shownSubject.dfmSummary
            if (!dfm) {
              return (
                <div className="part-config-feedback">
                  <p className="caption-text">
                    {capabilities.dfmCapable
                      ? 'Manufacturability analysis is still in progress.'
                      : "Manufacturability feedback isn't available at this stage."}
                  </p>
                </div>
              )
            }
            const findings = toDfmFindings(dfm.findings)
            return (
              <div className="part-config-feedback">
                <Chip tone={dfm.manufacturable ? 'success' : 'warning'}>
                  {dfm.manufacturable ? 'Manufacturable' : 'Needs review'}
                </Chip>
                {typeof dfm.indicativeLeadTimeDays === 'number' && (
                  <p className="caption-text">
                    Indicative lead time: {dfm.indicativeLeadTimeDays} business day
                    {dfm.indicativeLeadTimeDays === 1 ? '' : 's'}
                  </p>
                )}
                {/* Code-review W2 (UID-163/C3): the save seam can return the
                    PRIOR dfmSummary with no pending/stale flag on the wire —
                    this component has no signal telling it whether what
                    it's showing was actually recomputed for the edit just
                    saved. This note is deliberately neutral: it doesn't say
                    "updated" or "recomputed", and it doesn't fabricate a
                    "still recomputing" spinner either — it just states the
                    true, always-safe fact that this is the latest completed
                    analysis and more may follow. */}
                <p className="caption-text part-config-feedback-freshness-note">
                  This reflects the latest completed manufacturability analysis for this part.
                  Manufacturability feedback may continue to update in the background.
                </p>
                <DfmDiscrepancyList findings={findings} />
              </div>
            )
          }

          if (activeId === 'drawing') {
            // UID-434/WS-D — the same capability-vs-data distinction the DFM
            // tab draws above: `!attachmentsCapable` means "never a concept
            // for this entry point" (RFQ/Cart, always this branch);
            // `!subject.attachmentsPartId` means "no real, addressable part
            // id YET" for an entry point that CAN otherwise support it. The
            // two used to collapse into one string; they no longer do.
            if (!capabilities.attachmentsCapable) {
              return (
                <div className="part-config-drawing-tab">
                  <p className="caption-text">Drawing analysis isn't available at this stage.</p>
                </div>
              )
            }
            if (!subject.attachmentsPartId) {
              return (
                <div className="part-config-drawing-tab">
                  <p className="caption-text">Drawing analysis is not available for this part.</p>
                </div>
              )
            }
            return (
              <div className="part-config-drawing-tab">
                <PartAttachmentsSection
                  partId={subject.attachmentsPartId}
                  {...(attachmentsService ? { service: attachmentsService } : {})}
                />
              </div>
            )
          }

          return (
            <div className="part-config-body">
              <div className="part-config-left">
                <div className="part-config-left-header">
                  <h2 className="part-config-left-title">Configuration</h2>
                  <div className="part-config-left-actions">
                    {capabilities.applyToMultiple && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="part-config-secondary-btn"
                        onClick={() => onApplyToMultiple?.(buildFormState())}
                        disabled={!onApplyToMultiple || saving || applyToMultipleBusy}
                        loading={applyToMultipleBusy}
                        title={
                          onApplyToMultiple
                            ? undefined
                            : 'Select more than one part from the library to use this action'
                        }
                      >
                        Apply to multiple parts
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="part-config-secondary-btn"
                      onClick={() => handleSave(false)}
                      loading={saving}
                    >
                      Save this configuration
                    </Button>
                  </div>
                </div>

                {capabilities.applyToMultiple && !onApplyToMultiple && (
                  <p className="caption-text part-config-apply-multi-note">
                    Select multiple parts from the library to apply this configuration to more than
                    one part.
                  </p>
                )}

                <div className="part-config-row">
                  <div className="part-config-field">
                    <label htmlFor={processId} className="part-config-field-label">
                      Process
                    </label>
                    <div className="part-config-select-wrap">
                      <select
                        id={processId}
                        className="part-config-select"
                        value={process}
                        onChange={(event) => handleProcessChange(event.target.value as PartProcess)}
                      >
                        {PROCESS_OPTIONS.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <DownOutlined aria-hidden="true" className="part-config-select-chevron" />
                    </div>
                    {fieldErrors.process && (
                      <FieldError id={`${processId}-error`}>{fieldErrors.process}</FieldError>
                    )}
                  </div>

                  <div className="part-config-field part-config-field-quantity">
                    <label htmlFor={quantityId} className="part-config-field-label">
                      Quantity
                    </label>
                    <div className="part-config-stepper">
                      <input
                        id={quantityId}
                        type="number"
                        min={QUANTITY_MIN}
                        max={QUANTITY_MAX}
                        value={quantity}
                        onChange={(event) => {
                          const next = Number(event.target.value)
                          setQuantity(Number.isFinite(next) ? next : QUANTITY_MIN)
                        }}
                      />
                      <div className="part-config-stepper-buttons">
                        <button
                          type="button"
                          className="part-config-stepper-btn"
                          aria-label="Increase quantity"
                          onClick={() => setQuantity((q) => Math.min(QUANTITY_MAX, q + 1))}
                        >
                          ▴
                        </button>
                        <button
                          type="button"
                          className="part-config-stepper-btn"
                          aria-label="Decrease quantity"
                          onClick={() => setQuantity((q) => Math.max(QUANTITY_MIN, q - 1))}
                        >
                          ▾
                        </button>
                      </div>
                    </div>
                    {fieldErrors.quantity && (
                      <FieldError id={`${quantityId}-error`}>{fieldErrors.quantity}</FieldError>
                    )}
                  </div>
                </div>

                {/* ===== TECHNICAL DRAWING (optional) =====
                    UID-225 reconciliation: the design's Technical drawing
                    accordion row is an upload control with its own
                    "Generate drawing"/"Upload drawing" actions — but this
                    app ALREADY has a dedicated "Drawing analysis" tab
                    (`PartAttachmentsSection`, UID-155/B3) that owns that
                    exact upload plumbing. This section is deliberately a
                    light POINTER to that tab instead — its completion
                    indicator reads the real `drawingFilename` field so it's
                    still an honest signal, not a stub that always reads
                    empty. */}
                <section className="part-config-section" aria-labelledby={technicalHeadingId}>
                  <h3 className="part-config-accordion-heading">
                    <AccordionHeading
                      id={technicalHeadingId}
                      panelId={technicalPanelId}
                      title="Technical drawing"
                      optional
                      done={Boolean(subject.drawingFilename)}
                      expanded={openSections.technical}
                      onToggle={() => toggleSection('technical')}
                    />
                  </h3>
                  {openSections.technical && (
                    <div id={technicalPanelId} className="part-config-accordion-panel">
                      {subject.drawingFilename ? (
                        <>
                          <p className="caption-text">{`Attached: ${subject.drawingFilename}.`}</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setActiveTab('drawing')}
                          >
                            Go to Drawing analysis
                          </Button>
                        </>
                      ) : (
                        /* UID-511 — restores the design's empty-state visuals
                           (dc.html:5433–5462: two icon buttons, the
                           supported-format caption, the "Pro tip!" callout)
                           on top of the UID-225 pointer-to-tab decision. Both
                           buttons still just switch to the Drawing analysis
                           tab (`PartAttachmentsSection` owns the real
                           upload/generate plumbing) — this is a visual-parity
                           fix, not a re-implementation of that plumbing. */
                        <>
                          <div className="part-config-drawing-actions">
                            <button
                              type="button"
                              className="btn btn-outline part-config-drawing-btn"
                              onClick={() => setActiveTab('drawing')}
                            >
                              <EditOutlined aria-hidden="true" />
                              Generate drawing
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline part-config-drawing-btn"
                              onClick={() => setActiveTab('drawing')}
                            >
                              <UploadOutlined aria-hidden="true" />
                              Upload drawing
                            </button>
                          </div>
                          <p className="caption-text part-config-drawing-support-note">
                            Supported file format: .pdf
                          </p>
                          <div className="part-config-drawing-pro-tip">
                            <BulbOutlined
                              aria-hidden="true"
                              className="part-config-drawing-pro-tip-icon"
                            />
                            <p>
                              <strong>Pro tip!</strong> Include technical callouts in your drawing
                              such as process, material, finish, masking, and certificates to
                              automatically configure your part.
                            </p>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </section>

                {/* ===== MATERIAL (required) ===== */}
                <section className="part-config-section" aria-labelledby={materialHeadingId}>
                  <h3 className="part-config-accordion-heading">
                    <AccordionHeading
                      id={materialHeadingId}
                      panelId={materialPanelId}
                      title="Material"
                      done={materialChosen}
                      expanded={openSections.material}
                      onToggle={() => toggleSection('material')}
                    />
                  </h3>
                  {!openSections.material && materialChosen && (
                    <p className="part-config-accordion-summary">{material}</p>
                  )}
                  {openSections.material && (
                    <div id={materialPanelId} className="part-config-accordion-panel">
                      {/* UID-225 (CSS-fidelity review, NOTABLE): "Search
                          materials" is redundant next to the "Material"
                          heading right above it — kept as the combobox's
                          real accessible name (never removed, WCAG 1.3.1)
                          but visually subordinated to a small caption rather
                          than competing with the section heading at the same
                          weight/size. */}
                      <label htmlFor={materialInputId} className="part-config-material-label">
                        Search materials
                      </label>
                      <div className="part-config-combobox">
                        <SearchOutlined
                          aria-hidden="true"
                          className="part-config-combobox-icon-leading"
                        />
                        <input
                          id={materialInputId}
                          role="combobox"
                          type="text"
                          autoComplete="off"
                          className="part-config-combobox-input"
                          aria-expanded={materialOpen}
                          aria-controls={materialListId}
                          aria-autocomplete="list"
                          aria-activedescendant={
                            materialOpen && filteredMaterials[materialHighlight]
                              ? `${materialListId}-opt-${filteredMaterials[materialHighlight]?.id}`
                              : undefined
                          }
                          placeholder="e.g. Aluminum 6061"
                          value={materialQuery}
                          onFocus={() => setMaterialOpen(true)}
                          onChange={(event) => {
                            setMaterialQuery(event.target.value)
                            setMaterialOpen(true)
                            setMaterialHighlight(0)
                            if (event.target.value.trim() === '') setMaterial('')
                          }}
                          onKeyDown={handleMaterialKeyDown}
                        />
                        <DownOutlined
                          aria-hidden="true"
                          className="part-config-combobox-icon-trailing"
                        />
                        {/* This is the ARIA "combobox with list autocomplete +
                            aria-activedescendant" pattern (WAI-ARIA APG) — a native
                            <select> can't render a live-filtered, free-text-searchable
                            option list, which is the whole point of this control. Focus
                            intentionally stays on the owning <input role="combobox"> the
                            whole time; the listbox/options are navigated via
                            aria-activedescendant (set on the input above), never given
                            DOM focus themselves. */}
                        {materialOpen && (
                          // biome-ignore lint/a11y/useFocusableInteractive: see the comment above.
                          <div
                            id={materialListId}
                            // biome-ignore lint/a11y/useSemanticElements: see the comment above.
                            role="listbox"
                            aria-label="Material options"
                            className="part-config-combobox-listbox"
                          >
                            {materialRows.length === 0 ? (
                              <div className="part-config-combobox-empty">
                                No materials match your search.
                              </div>
                            ) : (
                              materialRows.map((row) =>
                                row.kind === 'header' ? (
                                  // Non-interactive group label (the design's
                                  // uppercase `isHeader` row, dc.html:5471) —
                                  // `role="presentation"` keeps it OUT of the
                                  // listbox's option set so `aria-activedescendant`
                                  // arrow-nav never lands on a header, only on real
                                  // options.
                                  <div
                                    key={`header-${row.category}`}
                                    role="presentation"
                                    className="part-config-combobox-group-label"
                                  >
                                    {row.category}
                                  </div>
                                ) : (
                                  // biome-ignore lint/a11y/useFocusableInteractive: same combobox pattern as the listbox above.
                                  <div
                                    key={row.option.id}
                                    id={`${materialListId}-opt-${row.option.id}`}
                                    // biome-ignore lint/a11y/useSemanticElements: same combobox pattern as the listbox above.
                                    role="option"
                                    aria-selected={material === row.option.name}
                                    className={
                                      row.index === materialHighlight
                                        ? 'part-config-combobox-option part-config-combobox-option-highlighted'
                                        : 'part-config-combobox-option'
                                    }
                                    onMouseDown={(event) => {
                                      event.preventDefault()
                                      selectMaterial(row.option)
                                    }}
                                    onMouseEnter={() => setMaterialHighlight(row.index)}
                                  >
                                    <span>{row.option.name}</span>
                                    {material === row.option.name && (
                                      <CheckOutlined aria-hidden="true" />
                                    )}
                                  </div>
                                ),
                              )
                            )}
                          </div>
                        )}
                      </div>
                      {fieldErrors.material && (
                        <FieldError id={`${materialInputId}-error`}>
                          {fieldErrors.material}
                        </FieldError>
                      )}
                    </div>
                  )}
                </section>

                {/* ===== FINISH (required) ===== */}
                <section className="part-config-section" aria-labelledby={finishHeadingId}>
                  <h3 className="part-config-accordion-heading">
                    <AccordionHeading
                      id={finishHeadingId}
                      panelId={finishPanelId}
                      title="Finish"
                      done={finish.trim().length > 0}
                      expanded={openSections.finish}
                      onToggle={() => toggleSection('finish')}
                    />
                  </h3>
                  {!openSections.finish && finish.trim().length > 0 && (
                    <p className="part-config-accordion-summary">{finish}</p>
                  )}
                  {openSections.finish && (
                    <div id={finishPanelId} className="part-config-accordion-panel">
                      {!materialChosen ? (
                        <p className="caption-text">Select a material to see finish options.</p>
                      ) : (
                        <>
                          {/* UID-502 — dc.html:5483-5509 tile grid, replacing
                              the native `<select>`s: each finish is a
                              `role="radio"` tile; a color-capable finish's
                              own colors render as chips INSIDE that finish's
                              tile, only while it is the selected one
                              (`capabilities.colorCapable` still gates the
                              whole color affordance — ADR-0117 D9.1). No
                              visible "Finish" label above the grid (the
                              accordion's own "Finish" heading already says
                              this) — `aria-label` keeps the accessible name
                              for the radiogroup without a redundant visible
                              one. */}
                          <div
                            role="radiogroup"
                            aria-label="Finish"
                            className="part-config-finish-tiles"
                          >
                            {finishTileOptions.map((option, index) => {
                              const selected = finish === option.name
                              const isAsMachined = option.name === AS_MACHINED_FINISH
                              const tileColors = option.colors ?? []
                              const showColors =
                                capabilities.colorCapable && selected && tileColors.length > 0
                              // UID-503/UID-505 — the SAME per-finish adder the
                              // live pricing estimate already charges
                              // (`mock-configuration-pricing-port.ts`'s
                              // `#finishAdder`, now delegating to this one
                              // function too), so the tile's tag can never
                              // drift from what selecting it actually costs.
                              // Always 0 for the as-machined tile — it is not
                              // a real catalog entry `finishPriceAdderFor`
                              // has ever heard of.
                              const priceAdder = isAsMachined ? 0 : finishPriceAdderFor(option.name)
                              return (
                                <div
                                  key={option.id}
                                  ref={(node) => {
                                    finishTileRefs.current[index] = node
                                  }}
                                  // biome-ignore lint/a11y/useSemanticElements: can't be a real radio input — a selected tile nests its own role="radio" color chips, and interactive controls can't nest inside a native radio's wrapping <label>.
                                  role="radio"
                                  // UID-503 — an explicit name, now that the tile
                                  // also carries a price tag + description
                                  // (content-derived naming would announce all
                                  // of that run together as the radio's name).
                                  aria-label={option.name}
                                  aria-checked={selected}
                                  tabIndex={selected || (!finish && index === 0) ? 0 : -1}
                                  className={
                                    selected
                                      ? 'part-config-finish-tile part-config-finish-tile-selected'
                                      : 'part-config-finish-tile'
                                  }
                                  onClick={() => selectFinish(option)}
                                  onKeyDown={(event) => handleFinishTileKeyDown(event, index)}
                                >
                                  <span className="part-config-finish-tile-header">
                                    <span className="part-config-finish-tile-name">
                                      {option.name}
                                    </span>
                                    <span className="part-config-finish-tile-trailing">
                                      {priceAdder > 0 && (
                                        <span className="part-config-finish-tile-price">
                                          +${priceAdder}/ea
                                        </span>
                                      )}
                                      {selected && <CheckOutlined aria-hidden="true" />}
                                    </span>
                                  </span>
                                  <span className="part-config-finish-tile-desc">
                                    {isAsMachined
                                      ? AS_MACHINED_DESCRIPTION
                                      : FINISH_TILE_DESCRIPTION}
                                  </span>
                                  {showColors && (
                                    <div
                                      role="radiogroup"
                                      aria-label="Color"
                                      className="part-config-finish-tile-colors"
                                    >
                                      {tileColors.map((colorOption) => {
                                        const colorSelected = color === colorOption.name
                                        return (
                                          <button
                                            key={colorOption.id}
                                            type="button"
                                            // biome-ignore lint/a11y/useSemanticElements: same reasoning as the parent tile — lives inside a non-native role="radio" container, not a <label>-wrapped native radio group.
                                            role="radio"
                                            aria-checked={colorSelected}
                                            className={
                                              colorSelected
                                                ? 'part-config-finish-color-chip part-config-finish-color-chip-selected'
                                                : 'part-config-finish-color-chip'
                                            }
                                            onClick={(event) => {
                                              // Stop this from also bubbling
                                              // into the tile's own onClick
                                              // above, which would immediately
                                              // reset the color it just set.
                                              event.stopPropagation()
                                              setColor(colorOption.name)
                                            }}
                                          >
                                            {colorSelected && <CheckOutlined aria-hidden="true" />}
                                            {colorOption.name}
                                          </button>
                                        )
                                      })}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="part-config-custom-finish-btn"
                          >
                            Request custom finish
                          </Button>
                        </>
                      )}
                      {fieldErrors.finish && (
                        <FieldError id={`${finishId}-error`}>{fieldErrors.finish}</FieldError>
                      )}
                    </div>
                  )}
                </section>

                {/* ===== THREADED HOLES (optional) =====
                    UID-434/WS-D — `threadedHoleCount` is not on
                    `RfqLineSpecification` (ADR-0117 D9.1); the whole section
                    is OMITTED (not rendered read-only) for an entry point
                    that cannot persist it — there is no stored value to
                    reflect and no server field to save one into. */}
                {capabilities.threadedHolesCapable && (
                  <section className="part-config-section" aria-labelledby={threadsHeadingId}>
                    <h3 className="part-config-accordion-heading">
                      <AccordionHeading
                        id={threadsHeadingId}
                        panelId={threadsPanelId}
                        title="Threaded holes"
                        optional
                        done={threadedHoleCount !== null}
                        expanded={openSections.threads}
                        onToggle={() => toggleSection('threads')}
                      />
                    </h3>
                    {!openSections.threads && threadedHoleCount !== null && (
                      <p className="part-config-accordion-summary">
                        {threadedHoleCount} threaded hole{threadedHoleCount === 1 ? '' : 's'}
                      </p>
                    )}
                    {openSections.threads && (
                      <div id={threadsPanelId} className="part-config-accordion-panel">
                        {/* Honest manual-entry copy (UID-437): this app has NO
                            thread-detection capability — `threadedHoleCount` is
                            a saved user input, not a geometry-derived signal —
                            so the copy never claims "we detected N". A real
                            detection pipeline (server-derived count + a separate
                            confirmed field) is a distinct future story. */}
                        <p className="caption-text">
                          {initial.threadedHoleCount !== null
                            ? `This part is set to ${initial.threadedHoleCount} threaded hole${initial.threadedHoleCount === 1 ? '' : 's'}. Adjust the count so the shop cuts them to spec.`
                            : 'Add the number of threaded holes on this part so the shop can cut them to spec.'}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="part-config-configure-threads-btn"
                          aria-expanded={threadsFieldsOpen}
                          aria-controls={`${threadsPanelId}-fields`}
                          onClick={() => setThreadsFieldsOpen((v) => !v)}
                        >
                          Configure threads
                        </Button>
                        {threadsFieldsOpen && (
                          <div
                            id={`${threadsPanelId}-fields`}
                            className="part-config-threads-panel"
                          >
                            <label htmlFor={threadsInputId} className="part-config-field-label">
                              Threaded hole count
                            </label>
                            <div className="part-config-stepper">
                              <input
                                id={threadsInputId}
                                type="number"
                                min={THREADED_HOLE_MIN}
                                max={THREADED_HOLE_MAX}
                                value={threadedHoleCount ?? 0}
                                onChange={(event) => {
                                  const next = Number(event.target.value)
                                  setThreadedHoleCount(
                                    Number.isFinite(next) ? Math.max(THREADED_HOLE_MIN, next) : 0,
                                  )
                                }}
                              />
                              <div className="part-config-stepper-buttons">
                                <button
                                  type="button"
                                  className="part-config-stepper-btn"
                                  aria-label="Increase threaded hole count"
                                  onClick={() =>
                                    setThreadedHoleCount((c) =>
                                      Math.min(THREADED_HOLE_MAX, (c ?? 0) + 1),
                                    )
                                  }
                                >
                                  ▴
                                </button>
                                <button
                                  type="button"
                                  className="part-config-stepper-btn"
                                  aria-label="Decrease threaded hole count"
                                  onClick={() =>
                                    setThreadedHoleCount((c) =>
                                      Math.max(THREADED_HOLE_MIN, (c ?? 0) - 1),
                                    )
                                  }
                                >
                                  ▾
                                </button>
                              </div>
                            </div>
                            {fieldErrors.threadedHoleCount && (
                              <FieldError id={`${threadsInputId}-error`}>
                                {fieldErrors.threadedHoleCount}
                              </FieldError>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                )}

                {/* ===== INSPECTIONS (optional) ===== */}
                {/* Plain `<section>`/`<h3>` (identical to every sibling
                    section), NOT `<fieldset>`/`<legend>`: the options are now
                    Add/Added toggle BUTTONS, not a checkbox group needing
                    fieldset grouping semantics. A `<legend>` renders straddling
                    the box's top edge, carving a GAP into this section's
                    `border-top` divider — so the Inspections/Certificates
                    separators rendered broken while `<section>` siblings drew
                    clean lines. Uniform markup fixes the divider (UID-437). */}
                <section className="part-config-section" aria-labelledby={inspectionsHeadingId}>
                  <h3 className="part-config-accordion-heading">
                    <AccordionHeading
                      id={inspectionsHeadingId}
                      panelId={inspectionsPanelId}
                      title="Inspections"
                      optional
                      done={inspections.length > 0 || customInspection}
                      expanded={openSections.inspections}
                      onToggle={() => toggleSection('inspections')}
                    />
                  </h3>
                  {!openSections.inspections && (inspections.length > 0 || customInspection) && (
                    <p className="part-config-accordion-summary">
                      {[...inspections, ...(customInspection ? ['Custom inspection'] : [])].join(
                        ', ',
                      )}
                    </p>
                  )}
                  {openSections.inspections && (
                    <div id={inspectionsPanelId} className="part-config-accordion-panel">
                      {/* UID-502 — always shown, regardless of
                          `materialChosen`: it's not a catalog option, just an
                          informational statement of what every quote already
                          includes (see the constant's doc comment above). */}
                      <div className="part-config-option-row part-config-option-row-standard">
                        <div className="part-config-option-copy">
                          <span className="part-config-option-name">
                            {STANDARD_INSPECTION_NAME}
                          </span>
                          <span className="part-config-option-desc">
                            {STANDARD_INSPECTION_DESCRIPTION}
                          </span>
                        </div>
                        <span className="part-config-option-included">Free</span>
                      </div>
                      {!materialChosen ? (
                        <p className="caption-text">
                          Select a material to see available inspections.
                        </p>
                      ) : availableInspections.length === 0 ? (
                        <p className="caption-text">No inspections available for this material.</p>
                      ) : (
                        availableInspections.map((option) => {
                          const selected = inspections.includes(option)
                          return (
                            <div key={option} className="part-config-option-row">
                              <div className="part-config-option-copy">
                                <span className="part-config-option-name">{option}</span>
                                <span className="part-config-option-desc">
                                  {descriptionFor(
                                    option,
                                    INSPECTION_DESCRIPTIONS,
                                    DEFAULT_INSPECTION_DESCRIPTION,
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={
                                  selected ? 'part-config-option-added' : 'part-config-option-add'
                                }
                                aria-pressed={selected}
                                aria-label={selected ? `${option} added` : `Add ${option}`}
                                onClick={() =>
                                  setInspections((prev) => toggleListItem(prev, option))
                                }
                              >
                                {selected ? (
                                  <>
                                    <CheckOutlined aria-hidden="true" />
                                    Added
                                  </>
                                ) : (
                                  '+ Add'
                                )}
                              </button>
                            </div>
                          )
                        })
                      )}
                      {/* UID-434/WS-D — RFQ-only routing state
                          (`RfqLineSpecification.customInspection`), never a
                          catalog membership; material-INDEPENDENT (unlike the
                          options above), so it renders regardless of
                          `materialChosen`/`availableInspections`. Absent for
                          Part Library — `EditPartConfigRequest` has no field
                          to save a `true` value into. Same Add/Added row
                          treatment as the catalog options for visual
                          consistency (UID-437). */}
                      {capabilities.customInspectionCapable && (
                        <div className="part-config-option-row">
                          <span className="part-config-option-name">Custom inspection</span>
                          <button
                            type="button"
                            className={
                              customInspection
                                ? 'part-config-option-added'
                                : 'part-config-option-add'
                            }
                            aria-pressed={customInspection}
                            aria-label={
                              customInspection ? 'Custom inspection added' : 'Add custom inspection'
                            }
                            onClick={() => setCustomInspection((v) => !v)}
                          >
                            {customInspection ? (
                              <>
                                <CheckOutlined aria-hidden="true" />
                                Added
                              </>
                            ) : (
                              '+ Add'
                            )}
                          </button>
                        </div>
                      )}
                      {fieldErrors.inspections && (
                        <FieldError id="inspections-error">{fieldErrors.inspections}</FieldError>
                      )}
                    </div>
                  )}
                </section>

                {/* ===== CERTIFICATES (optional) ===== */}
                {/* `<section>`/`<h3>` for the same reason as Inspections above
                    (Add/Added toggle buttons, not a checkbox group; avoids the
                    `<legend>` gap in the `border-top` divider — UID-437). */}
                <section className="part-config-section" aria-labelledby={certificatesHeadingId}>
                  <h3 className="part-config-accordion-heading">
                    <AccordionHeading
                      id={certificatesHeadingId}
                      panelId={certificatesPanelId}
                      title="Certificates"
                      optional
                      done={certificates.length > 0}
                      expanded={openSections.certificates}
                      onToggle={() => toggleSection('certificates')}
                    />
                  </h3>
                  {!openSections.certificates && certificates.length > 0 && (
                    <p className="part-config-accordion-summary">{certificates.join(', ')}</p>
                  )}
                  {openSections.certificates && (
                    <div id={certificatesPanelId} className="part-config-accordion-panel">
                      {!materialChosen ? (
                        <p className="caption-text">
                          Select a material to see available certificates.
                        </p>
                      ) : !certificatesAllowed ? (
                        // UID-93 (real rule, CERTIFICATION_ALLOWED_PROCESS_TYPES):
                        // certificates are never offered for urethane_casting.
                        <p className="caption-text">
                          Certificates aren't offered for this process.
                        </p>
                      ) : availableCertificates.length === 0 ? (
                        <p className="caption-text">No certificates available for this material.</p>
                      ) : (
                        availableCertificates.map((option) => {
                          const selected = certificates.includes(option)
                          return (
                            <div key={option} className="part-config-option-row">
                              <div className="part-config-option-copy">
                                <span className="part-config-option-name">{option}</span>
                                <span className="part-config-option-desc">
                                  {descriptionFor(
                                    option,
                                    CERTIFICATE_DESCRIPTIONS,
                                    DEFAULT_CERTIFICATE_DESCRIPTION,
                                  )}
                                </span>
                              </div>
                              <button
                                type="button"
                                className={
                                  selected ? 'part-config-option-added' : 'part-config-option-add'
                                }
                                aria-pressed={selected}
                                aria-label={selected ? `${option} added` : `Add ${option}`}
                                onClick={() =>
                                  setCertificates((prev) => toggleListItem(prev, option))
                                }
                              >
                                {selected ? (
                                  <>
                                    <CheckOutlined aria-hidden="true" />
                                    Added
                                  </>
                                ) : (
                                  '+ Add'
                                )}
                              </button>
                            </div>
                          )
                        })
                      )}
                      {fieldErrors.certificates && (
                        <FieldError id="certificates-error">{fieldErrors.certificates}</FieldError>
                      )}
                    </div>
                  )}
                </section>

                {/* ===== ADDITIONAL REQUIREMENTS (optional) =====
                    UID-434/WS-D — `additionalRequirements` is not on
                    `RfqLineSpecification` (ADR-0117 D9.1); OMITTED (not
                    read-only) for an entry point that cannot persist it. */}
                {capabilities.additionalRequirementsCapable && (
                  <section className="part-config-section" aria-labelledby={additionalHeadingId}>
                    <h3 className="part-config-accordion-heading">
                      <AccordionHeading
                        id={additionalHeadingId}
                        panelId={additionalPanelId}
                        title="Additional requirements"
                        optional
                        done={additionalRequirements.trim().length > 0}
                        expanded={openSections.additional}
                        onToggle={() => toggleSection('additional')}
                      />
                    </h3>
                    {!openSections.additional && additionalRequirements.trim().length > 0 && (
                      <p className="part-config-accordion-summary">{additionalRequirements}</p>
                    )}
                    {openSections.additional && (
                      <div id={additionalPanelId} className="part-config-accordion-panel">
                        <label htmlFor={additionalReqId} className="part-config-field-label">
                          Additional requirements
                        </label>
                        <textarea
                          id={additionalReqId}
                          maxLength={ADDITIONAL_REQUIREMENTS_MAX}
                          value={additionalRequirements}
                          onChange={(event) => setAdditionalRequirements(event.target.value)}
                          placeholder="Describe your requirements"
                        />
                        {fieldErrors.additionalRequirements && (
                          <FieldError id={`${additionalReqId}-error`}>
                            {fieldErrors.additionalRequirements}
                          </FieldError>
                        )}
                      </div>
                    )}
                  </section>
                )}
              </div>

              <div className="part-config-right">
                <div className="part-config-viewer-panel">
                  <ConfigViewerPlaceholder subject={shownSubject} />
                  {/* UID-225 — the design mock's viewer panel shows
                      illustrative dimensions ("2.99 x 2.22 x 0.28 in") as
                      static content; no dimensions field exists anywhere on
                      the contract today for any entry point. Rather than
                      fabricate a value or show a permanent "not available"
                      placeholder, the whole row (label included) is gated
                      on `shownSubject.dimensions` being present and
                      non-empty — it renders nothing today and will light up
                      automatically once an upstream contract change adds a
                      real dimensions field and a mapper populates it. */}
                  {shownSubject.dimensions && shownSubject.dimensions.trim().length > 0 && (
                    <div className="part-config-dimensions-row">
                      <span className="caption-text">Dimensions</span>
                      <span className="part-config-dimensions-value">
                        {shownSubject.dimensions}
                      </span>
                    </div>
                  )}
                </div>

                <PricingFooter
                  subject={shownSubject}
                  capabilities={capabilities}
                  form={buildFormState()}
                  initialFinish={initial.finish ? initial.finish : null}
                  partSeed={partSeed}
                  quantity={quantity}
                  onQuantityChange={setQuantity}
                  onAddToCart={onAddToCart ? handleAddToCart : undefined}
                  onSendToRfq={onSendToRfq}
                  onSaveAndClose={() => handleSave(true)}
                  saving={saving}
                  nextPart={onNextPart ? { onAdvance: handleNextPart } : undefined}
                />
              </div>
            </div>
          )
        }}
      </Tabs>
    </Modal>
  )
}

function FieldError({ id, children }: { readonly id: string; readonly children: string }) {
  return (
    <p id={id} role="alert" className="part-config-field-error">
      {children}
    </p>
  )
}

/**
 * One accordion section's clickable header — the WAI-ARIA "disclosure"
 * pattern (a real `<button>`, never a `<div onClick>`), nested inside the
 * section's `<h3>` heading by its caller. Every section (including
 * Inspections and Certificates, whose options are Add/Added toggle buttons
 * rather than a checkbox group) uses the same `<section>`/`<h3>` shell, so the
 * section divider (`border-top`) renders as a clean full-width line — a
 * `<legend>` would carve a gap into that top border.
 *
 * The completion indicator is never color-only (WCAG 1.4.1): "done" swaps
 * the SHAPE (filled circle + check glyph) not just the color, and carries
 * an `sr-only` "complete"/"incomplete" text equivalent alongside the
 * `aria-hidden` glyph.
 */
function AccordionHeading({
  id,
  panelId,
  title,
  optional = false,
  done,
  expanded,
  onToggle,
}: {
  readonly id: string
  readonly panelId: string
  readonly title: string
  readonly optional?: boolean
  readonly done: boolean
  readonly expanded: boolean
  readonly onToggle: () => void
}) {
  return (
    <button
      type="button"
      id={id}
      className="part-config-accordion-trigger"
      aria-expanded={expanded}
      aria-controls={panelId}
      onClick={onToggle}
    >
      <RightOutlined
        aria-hidden="true"
        className={
          expanded
            ? 'part-config-accordion-chevron part-config-accordion-chevron-open'
            : 'part-config-accordion-chevron'
        }
      />
      <span className="part-config-accordion-title">
        {title}
        {optional && <span className="caption-text"> (optional)</span>}
      </span>
      <span className="part-config-accordion-spacer" />
      <span
        className={
          done
            ? 'part-config-accordion-status part-config-accordion-status-done'
            : 'part-config-accordion-status part-config-accordion-status-empty'
        }
        aria-hidden="true"
      >
        {done && <CheckOutlined />}
      </span>
      <span className="sr-only">{done ? 'Complete' : 'Incomplete'}</span>
    </button>
  )
}

/**
 * Right column's viewer panel. Most demo parts here have no real CAD
 * geometry, so this renders the row's existing thumbnail treatment plus an
 * honest "preview unavailable" caption — never a fabricated model.
 *
 * UID-311 supersedes this component's prior "a real 3D engine is NOT to be
 * added" scoping for a SMALL, explicitly-canned set of demo parts
 * (`DEMO_SCENE_BY_PART_NAME`, keyed by `subject.sceneKey`): those render a
 * real, interactive `fictiv-3d-viewer` model using the library's own generic
 * test geometry, not real geometry for that part. Every other subject keeps
 * the honest fallback below unchanged.
 */
function ConfigViewerPlaceholder({ subject }: { readonly subject: PartConfiguratorSubject }) {
  const sceneBundle = DEMO_SCENE_BY_PART_NAME[subject.sceneKey ?? '']
  if (sceneBundle) {
    return (
      <div className="part-config-viewer-frame">
        <PartViewer3D sceneBundle={sceneBundle} />
      </div>
    )
  }

  return (
    <div className="part-config-viewer-frame">
      {subject.thumbnailUrl ? (
        <img src={subject.thumbnailUrl} alt="" className="part-config-viewer-thumb" />
      ) : (
        <FileTextOutlined aria-hidden="true" className="part-config-viewer-empty-icon" />
      )}
      <p className="caption-text part-config-viewer-note">
        {subject.thumbnailUrl
          ? "Live 3D preview isn't available for this part yet."
          : 'Preview unavailable — no drawing image on file.'}
      </p>
    </div>
  )
}

type PricingFooterState = 'notReady' | 'priced' | 'rfq'

/**
 * UID-225 — maps the SAME 3 states `PartsTable.tsx`'s `resolveRowStatus`/
 * `FILTER_STATUS_TONE` already derive for the listing onto this shell's
 * contextual pricing footer (`configView.notReady`/`.priced`/`.rfq`).
 * `pricingProjection.mode` is the primary signal; `rowStatus`/`filterStatus`
 * is only the fallback for a subject that hasn't been priced/analyzed at all
 * yet, mirroring `resolveRowStatus`'s own fallback chain rather than
 * inventing a second, independent status vocabulary.
 */
function resolvePricingFooterState(subject: PartConfiguratorSubject): PricingFooterState {
  const mode = subject.pricingProjection?.mode
  if (mode === 'auto_priced') return 'priced'
  if (mode === 'quote_required' || mode === 'locked_to_rfq') return 'rfq'
  const rowStatus =
    subject.rowStatus ??
    (subject.filterStatus === 'quote'
      ? 'needs_quote'
      : subject.filterStatus === 'config'
        ? 'needs_config'
        : 'ready')
  return rowStatus === 'needs_quote' ? 'rfq' : 'notReady'
}

/**
 * UI-only estimate derived from the real `dfmSummary.indicativeLeadTimeDays`
 * (business days) — NOT a wire field of its own. Returns `null` (never
 * renders a fabricated date) when the subject has no lead-time estimate yet.
 */
function estimateProducedByDate(indicativeLeadTimeDays: number | undefined): string | null {
  if (typeof indicativeLeadTimeDays !== 'number' || !Number.isFinite(indicativeLeadTimeDays)) {
    return null
  }
  const date = new Date()
  let remaining = Math.max(0, Math.round(indicativeLeadTimeDays))
  while (remaining > 0) {
    date.setDate(date.getDate() + 1)
    const day = date.getDay()
    if (day !== 0 && day !== 6) remaining -= 1
  }
  return formatDate(date.toISOString())
}

/**
 * UID-505 — resolves the finish adder for the WIRE value of `finish`
 * (`string | null`, where `null`/the "as machined" sentinel both mean "no
 * secondary operation"). Centralizes the sentinel-to-null translation so the
 * tile grid and the pricing footer can never disagree about which value
 * means "no finish."
 */
function finishAdderForWireValue(finish: string | null): number {
  if (finish === null || finish === AS_MACHINED_FINISH) {
    return 0
  }
  return finishPriceAdderFor(finish)
}

/**
 * UID-505 — adjusts a `Price` by a whole-dollar delta, staying a plain
 * decimal-string amount (never a float multiply) the way every other money
 * value in this shell is handled. `delta` may be negative (backing out the
 * ORIGINAL finish's adder before adding the CURRENTLY selected one back on).
 */
function adjustPriceAmount(price: Price, deltaDollars: number): Price {
  const amount = Number.parseFloat(price.amount)
  if (!Number.isFinite(amount)) return price
  return { ...price, amount: (amount + deltaDollars).toFixed(2) }
}

/**
 * The right column's contextual pricing footer — swaps between 3 mutually
 * exclusive states keyed off `resolvePricingFooterState`. `onAddToCart`/
 * `onSendToRfq` follow the same optional-callback-disabled-with-title
 * pattern as `onApplyToMultiple` (see this file's props doc) — absent, the
 * primary CTA renders disabled with an explanatory `title` rather than
 * silently inert or fabricating a mutation this shell doesn't own.
 *
 * UID-504 — the `'priced'` branch's dollar figure and "Add to cart" gate
 * used to read ONLY `subject.indicativePrice`, a snapshot frozen at the
 * moment the modal opened (`EditConfigurationModal.tsx` memoizes `subject`
 * on the `part` prop alone, by design, so a save-in-session doesn't clobber
 * in-progress edits — see that file's doc comment). Clicking a Finish tile
 * updated the live `finish` state correctly, but nothing downstream ever
 * re-read it for pricing, so the price/CTA looked frozen.
 *
 * UID-505 — the FIRST fix for that (UID-504) called the live, RFQ-agnostic
 * `POST /pricing/configuration-estimate` endpoint. That made things WORSE:
 * that endpoint prices from an entirely different model
 * (`categoryTier(process, material) + finishAdder(finish)`, the demo rate
 * card) than `subject.indicativePrice` (an arbitrary per-part value from
 * `part-analysis-service`'s demo fixture data, never derived from the rate
 * card at all) — so switching to it could make the displayed price DROP
 * when the buyer had just added a paid finish, which is worse than frozen.
 *
 * Fixed properly here: `subject.indicativePrice` stays the one source for
 * the BASE dollar figure (never replaced by an unrelated recomputation).
 * The finish's own $ delta — the SAME number already shown on its tile,
 * `finishPriceAdderFor` — is backed out for whatever finish this price
 * already reflected (`initial.finish`) and added back in for whichever
 * finish is CURRENTLY selected, so switching finishes always moves the
 * price by exactly that finish's own adder, never an unrelated jump, and
 * needs no network call.
 */
function ConfigPricingFooter({
  subject,
  capabilities,
  form,
  initialFinish,
  quantity,
  onQuantityChange,
  onAddToCart,
  onSendToRfq,
  onSaveAndClose,
  saving,
}: PartConfiguratorPricingFooterProps) {
  const state = resolvePricingFooterState(subject)
  const projection = subject.pricingProjection

  if (state === 'notReady') {
    return (
      <div className="part-config-pricing-footer">
        <div className="part-config-pricing-callout part-config-pricing-callout-info">
          <InfoCircleOutlined aria-hidden="true" />
          <p>Configure your part to receive pricing and lead time options.</p>
        </div>
      </div>
    )
  }

  if (state === 'rfq') {
    // `locked_to_rfq` (already committed to an in-flight RFQ) can't be
    // "sent to RFQ" again — that CTA would be a nonsensical duplicate
    // action, so it's omitted for that sub-case rather than always shown.
    const locked = projection?.mode === 'locked_to_rfq'
    // UID-434 human ruling (post-launch copy fix) — Cart lands in this same
    // footer state for a different, honest reason: a spec edit reset the
    // line's price, not "this configuration needs a manual quote". The
    // "Send to RFQ" CTA is dropped entirely for that case too (not merely
    // disabled) — Cart has no automatic RFQ hand-off to offer, wired or not,
    // and a disabled button beside the sentence would imply one more
    // strongly than the sentence itself does. See
    // `PartConfiguratorCapabilities.rfqFooterCopyKind`'s doc comment.
    const isCartLineReset = capabilities.rfqFooterCopyKind === 'cartLineReset'
    return (
      <div className="part-config-pricing-footer">
        <div className="part-config-pricing-callout part-config-pricing-callout-violet">
          <FileTextOutlined aria-hidden="true" />
          <p>
            {locked
              ? projection?.lockedRfqId
                ? `This part is locked to RFQ ${projection.lockedRfqId} and can't be re-priced.`
                : "This part is locked to an in-flight RFQ and can't be re-priced."
              : isCartLineReset
                ? "This line needs a new price after that change — we'll get you an updated quote."
                : "This configuration includes options that need a manual quote. We'll price it and send it back — usually within one business day."}
          </p>
        </div>
        {!locked && !isCartLineReset && (
          <button
            type="button"
            className="part-config-cta-rfq"
            onClick={onSendToRfq ? () => onSendToRfq(subject) : undefined}
            disabled={!onSendToRfq}
            title={onSendToRfq ? undefined : "Sending to RFQ from this modal isn't wired up yet"}
          >
            Send to RFQ
          </button>
        )}
        <Button
          variant="outline"
          size="lg"
          onClick={onSaveAndClose}
          loading={saving}
          className="part-config-cta-secondary"
        >
          Save and close
        </Button>
      </div>
    )
  }

  // UID-505 — `subject.indicativePrice` stays the ONE base figure; only a
  // FINISH-DELTA adjustment is layered on top (see this function's doc
  // comment for why a live re-estimate from a different pricing model was
  // tried and reverted). Backs out whatever finish the snapshot already
  // reflected, then adds back whichever finish is selected right now —
  // both reads of the exact same `finishPriceAdderFor` the tile itself
  // displays, so the number on screen and the delta applied here can never
  // disagree.
  const price = subject.indicativePrice
    ? adjustPriceAmount(
        subject.indicativePrice,
        finishAdderForWireValue(form.finish) - finishAdderForWireValue(initialFinish),
      )
    : null
  const priceLabel = price ? formatPrice(price) : '—'
  const producedBy = estimateProducedByDate(subject.dfmSummary?.indicativeLeadTimeDays)

  return (
    <div className="part-config-pricing-footer">
      <label className="part-config-pricing-qty">
        <span className="caption-text">Quantity</span>
        <input
          type="number"
          min={QUANTITY_MIN}
          max={QUANTITY_MAX}
          value={quantity}
          onChange={(event) => {
            const next = Number(event.target.value)
            onQuantityChange(Number.isFinite(next) ? next : QUANTITY_MIN)
          }}
        />
      </label>
      <div className="part-config-pricing-summary-row">
        {producedBy && (
          <div>
            <p className="caption-text">Produced as soon as</p>
            <p className="part-config-pricing-produced">{producedBy}</p>
          </div>
        )}
        <div className="part-config-pricing-price">
          <p className="caption-text">Qty {quantity}</p>
          <p className="part-config-pricing-price-value">{priceLabel}</p>
        </div>
      </div>
      <button
        type="button"
        className="part-config-cta-priced"
        onClick={onAddToCart ? () => onAddToCart(subject) : undefined}
        disabled={!onAddToCart || !price || saving}
        title={onAddToCart ? undefined : "Adding to cart from this modal isn't wired up yet"}
      >
        {price ? `Add to cart · ${priceLabel}` : 'Add to cart'}
      </button>
      <Button
        variant="outline"
        size="lg"
        onClick={onSaveAndClose}
        loading={saving}
        className="part-config-cta-secondary"
      >
        Save and close
      </Button>
    </div>
  )
}
